import { CreditCardOrganizationIcons } from '@cart-ui/CreditCardOrganizationIcons/CreditCardOrganizationIcons'
import { CARD_TYPE_TO_ORGANIZATION } from '@constants'
import { type WpUserPaymentToken } from '@dni-types/data-models/ecommerce-payment'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { Paper } from '@pets-ui/Paper/Paper'
import { Radio } from '@pets-ui/Radio/Radio'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { formatExpiryDate, isCardExpired, isCardholderInfoComplete } from '../../utils/payment-token'

interface PaymentTokenItemProps {
  token: WpUserPaymentToken
  isSelected: boolean
  isTapPay: boolean
  onSelect: () => void
  children?: ReactNode
}

export const PaymentTokenItem = (props: PaymentTokenItemProps) => {
  const { token, isSelected, isTapPay, onSelect, children } = props
  const { t } = useTranslation()

  const organization = CARD_TYPE_TO_ORGANIZATION[token.card_type]
  const issuingBankName = token.issuer_info.issuer_zh_tw || token.issuer_info.issuer
  const bankName = issuingBankName ? `${issuingBankName}  *${token.last4}` : `${token.card_type} *${token.last4}`
  const expired = isCardExpired(token.expiry_month, token.expiry_year, new Date())

  return (
    <Paper hasBorder borderRadius={8} elevation={0} backgroundColor={expired ? colors.grey100 : colors.white}>
      <VStack p={3}>
        <HStack gap={3} alignItems="center">
          <Radio
            color="blue"
            disabled={expired}
            checked={isSelected}
            onChange={onSelect}
          />
          <CreditCardOrganizationIcons organization={organization} />
          <VStack gap={1} flex={1} alignItems="flex-start">
            <Text variant="subtitle" color={expired ? colors.grey400 : undefined} bold>
              {bankName}
            </Text>
            <Text variant="caption" color={expired ? colors.grey400 : colors.grey600}>
              {expired
                ? t('wpMyAccount.expired', { defaultValue: '已過期' })
                : t('wpMyAccount.validUntil', {
                  date: formatExpiryDate(token.expiry_month, token.expiry_year),
                  defaultValue: '效期至 {{date}}',
                })}
            </Text>
            {token.is_default && (
              <Chip size="small">
                {t('wpMyAccount.defaultCardLabel', { defaultValue: '預設' })}
              </Chip>
            )}
            {isTapPay && !isCardholderInfoComplete(token.cardholder) && (
              <Chip size="small" color="red">
                {t('wpMyAccount.missingCardholderInfo', { defaultValue: '缺少持卡人資訊' })}
              </Chip>
            )}
          </VStack>
        </HStack>
        {children}
      </VStack>
    </Paper>
  )
}
